import React, { SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Add, Check, FilterList } from "@mui/icons-material";
import { Autocomplete, Box, Checkbox, ListItemIcon, ListItemText, ListSubheader, SelectChangeEvent, TextField } from "@mui/material";

import analytics from "fond/analytics";
import { ReturnType } from "fond/hooks/useFilterLayers";
import { Store } from "fond/types";

import { MenuItem, Select } from "./LayerFilter.styles";

export type LayerFilterOption =
  | { ID: string; Label: string; Type: "Option" }
  | { ID: string; Label: string; Type: "Category" }
  | { ID: string; Label: string; Type: "Autocomplete"; Options: { ID: string; Label: string }[] };

export type LayerFilterProps = {
  id: string;
  multiple?: boolean;
  options: LayerFilterOption[];
  filteredLayerIds: string[];
} & Pick<ReturnType, "onChange">;

const propagationKeys = ["ArrowUp", "ArrowDown", "Escape", "Enter", "PageUp", "PageDown"];

type CheckboxItemProps = {
  multiple: boolean;
  checked: boolean;
  option: { ID: string; Label: string };
  onClick: (event: React.MouseEvent<HTMLLIElement>) => void;
};

const CheckboxItem: React.FC<CheckboxItemProps> = ({ option, multiple, checked, onClick }) => {
  return (
    <MenuItem onClick={onClick} value={option.ID} dense role="option" aria-selected={checked}>
      <ListItemIcon>
        {multiple && <Checkbox checked={checked} disableRipple />}
        {!multiple && checked && <Check />}
      </ListItemIcon>
      <ListItemText primary={option.Label} />
    </MenuItem>
  );
};

const LayerFilter: React.FC<LayerFilterProps> = ({ id, filteredLayerIds, onChange, options, multiple = false }: LayerFilterProps) => {
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

  const handleAutocompleteInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string) => {
      setAutocompleteInputValue(newInputValue);
    },
    [setAutocompleteInputValue]
  );

  const optionIds = useMemo(
    () =>
      options.flatMap((option) => {
        if (option.Type === "Option") {
          return [option.ID];
        } else if (option.Type === "Autocomplete") {
          return option.Options.map((opt) => opt.ID);
        } else {
          return [];
        }
      }),
    [options]
  );

  const filteredLayerIdsLookup = useMemo(() => new Set(filteredLayerIds), [filteredLayerIds]);

  const projectId = useSelector((state: Store) => state.project.projectId);
  const handleOnLayerSelect = useCallback(
    (layerId: string) => (event: React.MouseEvent<HTMLLIElement>) => {
      event.stopPropagation();
      // If the layer is already selected, then we're hiding it.
      const show = !filteredLayerIdsLookup.has(layerId);
      onChange(id, layerId, optionIds, multiple, show);
    },
    [onChange, id, optionIds, multiple, filteredLayerIdsLookup]
  );

  const handleAutocompleteChange = useCallback(
    (_event: SyntheticEvent, newValue: { ID: string; Label: string } | null) => {
      if (newValue) {
        // The auto-complete only shows options that are not already selected.
        onChange(id, newValue.ID, optionIds, multiple, true);
        // Clear the input text after selection
        setAutocompleteInputValue("");
      }
    },
    [onChange, id, optionIds, multiple]
  );

  const onLayerFilterChange = (event: SelectChangeEvent<unknown>) => {
    // The 'autocomplete' option in the select doesn't have a value, so filter that entry out to avoid sadness.
    const value = event.target.value as (string | undefined)[];
    analytics.track("legend.layer-filter.changed", {
      id,
      projectId,
      value: value.filter((v) => v !== undefined),
    });
  };

  const getMenuItem = (option: LayerFilterOption) => {
    if (option.Type === "Category") {
      return (
        <ListSubheader key={option.ID} sx={{ fontSize: 13, lineHeight: 2, color: "grey" }}>
          {option.Label}
        </ListSubheader>
      );
    }

    if (option.Type === "Option") {
      const checked = filteredLayerIds?.includes(option.ID);
      return <CheckboxItem key={option.ID} checked={checked} option={option} multiple={multiple} onClick={handleOnLayerSelect(option.ID)} />;
    }

    if (option.Type === "Autocomplete") {
      const checkedOptions = option.Options.filter((opt) => filteredLayerIdsLookup?.has(opt.ID));
      const uncheckedOptions = option.Options.filter((opt) => !filteredLayerIdsLookup?.has(opt.ID));
      return (
        <React.Fragment key={`autocomplete-${option.ID}`}>
          {checkedOptions.length > 0 &&
            checkedOptions.map((checkedOption) => (
              <CheckboxItem
                key={checkedOption.ID}
                checked
                option={checkedOption}
                multiple={multiple}
                onClick={handleOnLayerSelect(checkedOption.ID)}
              />
            ))}
          {uncheckedOptions.length > 0 && (
            <MenuItem key={`${option.ID}-autocomplete`} value="" sx={{ minWidth: 270 }}>
              <Autocomplete
                size="small"
                options={uncheckedOptions}
                getOptionLabel={(option) => option.Label}
                onChange={handleAutocompleteChange}
                inputValue={autocompleteInputValue}
                onInputChange={handleAutocompleteInputChange}
                autoComplete
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search providers..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <Add sx={{ fontSize: 16 }} />,
                    }}
                    onKeyDown={(event) => {
                      // The Autocomplete is rendered inside a Select component, and they have fights over keyboard
                      // events. We attempt to control the madness here by blocking certain keys from propagating out of
                      // the autocomplete.
                      if (!propagationKeys.includes(event.key)) {
                        event.stopPropagation();
                      }
                    }}
                  />
                )}
                value={null}
                clearOnBlur
                clearOnEscape
                sx={{ width: "100%" }}
              />
            </MenuItem>
          )}
        </React.Fragment>
      );
    }

    return null;
  };

  return (
    <Select
      displayEmpty
      size="small"
      multiple
      onChange={onLayerFilterChange}
      value={filteredLayerIds ?? []}
      renderValue={() => {
        const value = optionIds.length - (filteredLayerIds?.length || 0);
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FilterList color="primary" sx={{ fontSize: (theme) => theme.typography.size.medium, mr: 0.5 }} />{" "}
            {value > 0 ? `Filters (${value})` : "Filter"}
          </Box>
        );
      }}
    >
      {options.map(getMenuItem)}
    </Select>
  );
};

export default LayerFilter;
